import {isLoggedIn} from '@github-ui/client-env'
import {sendEvent} from '@github-ui/hydro-analytics'
import {sendStats} from '@github-ui/stats'

/**
 * Nudge category for the Android app-install banner.
 *
 * The `general_ipm_` prefix is load-bearing. The in-product-messaging dbt models
 * (`impressions_all`, `clicks_all`, `dismissals_all`) select Hydro browser events by an
 * explicit category allowlist plus a set of prefix matches including `LIKE 'general_ipm%'`,
 * so a category without one of those prefixes never reaches the reporting tables.
 */
export const ANALYTICS_CATEGORY = 'general_ipm_android_app_install_banner'

/** The five funnel steps, plus the install-state signal that is not part of the funnel. */
export type BannerAction =
  | 'visible'
  | 'get_app_click'
  | 'prompt_accepted'
  | 'prompt_dismissed'
  | 'dismiss'
  | 'app_detected_installed'

type BannerEvent = {
  /** Aggregate Datadog counter. Retained alongside Hydro for dashboards and alerting. */
  incrementKey: string
  /**
   * Hydro event type.
   *
   * Only `analytics.visible` and `analytics.click` feed the shared reporting tables.
   * `clicks_all` selects every `analytics.click` whose action is not `dismiss`, so routing
   * the prompt outcomes through `analytics.click` would silently inflate the click count
   * roughly threefold. They get their own event type instead and are queried directly from
   * `analytics_v0_browser_event`.
   */
  hydroType: string
  /**
   * Whether the event may wait for the next idle flush. Actions immediately followed by
   * Chrome's native prompt or a hand-off to the Play Store can background the tab before
   * that flush runs, so they are sent straight away.
   */
  batched: boolean
}

const BANNER_EVENTS = {
  visible: {
    incrementKey: 'APP_INSTALL_BANNER_VISIBLE',
    hydroType: 'analytics.visible',
    batched: true,
  },
  // eslint-disable-next-line camelcase
  get_app_click: {
    incrementKey: 'APP_INSTALL_BANNER_GET_APP_CLICK',
    hydroType: 'analytics.click',
    batched: false,
  },
  dismiss: {
    incrementKey: 'APP_INSTALL_BANNER_DISMISS',
    hydroType: 'analytics.click',
    batched: true,
  },
  // eslint-disable-next-line camelcase
  prompt_accepted: {
    incrementKey: 'APP_INSTALL_BANNER_PROMPT_ACCEPTED',
    hydroType: 'android_app_install_banner.prompt_outcome',
    batched: false,
  },
  // eslint-disable-next-line camelcase
  prompt_dismissed: {
    incrementKey: 'APP_INSTALL_BANNER_PROMPT_DISMISSED',
    hydroType: 'android_app_install_banner.prompt_outcome',
    batched: true,
  },
  /**
   * Reports device install state on mount rather than an interaction with the banner, so it
   * is neither an impression nor a click and must not land in either table.
   */
  // eslint-disable-next-line camelcase
  app_detected_installed: {
    incrementKey: 'APP_INSTALL_BANNER_APP_DETECTED_INSTALLED',
    hydroType: 'android_app_install_banner.app_state',
    batched: true,
  },
} as const satisfies Record<BannerAction, BannerEvent>

/**
 * Records one banner action to both telemetry sinks.
 *
 * Every call site goes through here so the Datadog counter and the Hydro event can never
 * drift apart. The Hydro context is kept to the minimum the reporting queries need: the
 * category and action identify the nudge and funnel step, and `signed_in` distinguishes the
 * actor-keyed funnel from the signed-out reach cut without a second query. Actor and visitor
 * identifiers are not set here; Hydro attaches them from the page's existing base context.
 *
 * This function never throws. `get_app_click` is reported before `prompt()` is called on the
 * saved install event, and that call sits outside the caller's try/catch, so an exception
 * raised here would stop the user reaching the Play Store. Both sinks can raise in ways that
 * have nothing to do with the banner: `isLoggedIn()` throws when the client environment has
 * not loaded, and Hydro's base context parses the `visitor-payload` meta tag, which throws if
 * that tag is malformed. Telemetry is best-effort and must never break the install flow.
 */
export function trackBannerEvent(action: BannerAction): void {
  const event = BANNER_EVENTS[action]

  try {
    sendStats({incrementKey: event.incrementKey}, false, 1)
  } catch {
    // Best-effort: a counter failure must not suppress the Hydro event below.
  }

  try {
    sendEvent(
      event.hydroType,
      {
        category: ANALYTICS_CATEGORY,
        action,
        // eslint-disable-next-line camelcase
        signed_in: isLoggedIn(),
      },
      {batched: event.batched},
    )
  } catch {
    // Best-effort: see above.
  }
}
